'use client';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BoardHeader from './BoardHeader';
import MatchupRow from './MatchupRow';
import StandingsBoard from './StandingsBoard';
import LeagueSetup from './LeagueSetup';
import useLeagueRoute from './useLeagueRoute';
import useBoardFeed, { avatarPool } from './useBoardFeed';
import useBoardScale, { useFullscreen, useStandingsScale, BOARD_PAD_X } from './useBoardScale';
import ViewSwitch from './ViewSwitch';
import { BoardMasthead, SiteFooter } from './BoardLanding';
import {
  NoLeagueState, InvalidLeagueState, OffseasonState, LoadingState,
} from './BoardStates';

/* ============================================================
   SolariBoard — the app (§5, all five items composed).

   Ownership:
     league_id       LeagueSetup + useLeagueId (localStorage)
     data            useBoardFeed (central poll, diff, week rollover)
     scale           useBoardScale (viewport -> one `size` number)
     presentation    useFullscreen ('f' or the corner button)
     view            matchups | standings, with optional auto-rotation

   ROTATE is the TV mode's other half: unattended on a screen, the
   board alternates matchups and standings on a slow cycle. It is OFF by
   default and turned on explicitly (footer chip, or `a`). It used to
   switch itself on in fullscreen, which surprised people -- the board
   started moving on its own with nothing on screen explaining why so the display
   has something to do between polls. It is off by default when windowed
   and on in fullscreen — that's the difference between "a tab I'm
   watching" and "a screen in the room."
   ============================================================ */

const ROTATE_MS = 25000;

// Memoized so an unchanged row object (see useBoardFeed's reconcile) costs
// nothing on a poll where only one score moved.
const Row = memo(MatchupRow);

export default function SolariBoard({
  leagueId: leagueIdProp = null,   // override persistence (demo/embed)
  interval,
  fetchImpl,
  autoRotate: autoRotateProp,
  // 'api' (deployed, cached, shared) | 'sleeper' (direct from browser).
  // The preview harness injects a mock fetch and wants the direct path.
  source = fetchImpl ? 'sleeper' : 'api',
  apiBase,
}) {
  const stored = useLeagueRoute({ initial: leagueIdProp });
  const routeUsername = stored.username;
  const leagueId = leagueIdProp || stored.leagueId;

  const shellRef = useRef(null);
  const { isFullscreen, toggle } = useFullscreen(shellRef);

  const { board, week, status, error, lastUpdated, refresh } =
    useBoardFeed(leagueId, { interval, fetchImpl, source, apiBase });

  const [view, setView] = useState('matchups');
  // A manual toggle is a decision, not a nudge. Once the viewer picks a view
  // -- by clicking the ViewSwitch or pressing `s` -- auto-rotation stops for
  // good, and the board stays where they put it. Without this, rotation
  // yanked the standings away ~10s after they asked for it.
  // ROTATION IS NOW EXPLICIT.
  //
  // It used to be implicit: on in fullscreen, off the moment you touched
  // anything, with no way back except leaving fullscreen and returning.
  // That was surprising in both directions -- it started without being
  // asked and stopped without being told, and the only signal either had
  // happened was the board's own behavior 25 seconds later.
  //
  // `rotate` is a real piece of state with a real control (ROTATE in the
  // footer). Picking a view still stops rotation -- a deliberate choice
  // should not be overridden 25s later -- but now the chip shows that it
  // stopped, and clicking it starts things again.
  const [rotate, setRotate] = useState(false);
  const [userPinned, setUserPinned] = useState(false);
  const pinView = useCallback((next) => {
    setUserPinned(true);
    setRotate(false);          // an explicit choice turns rotation off
    setView((v) => (typeof next === 'function' ? next(v) : next));
  }, []);
  const toggleRotate = useCallback(() => {
    setRotate((r) => {
      if (!r) setUserPinned(false);   // turning it on clears the pin
      return !r;
    });
  }, []);
  // A finished season has standings but no live matchups. Opening on an
  // empty matchup grid would look broken, so the board lands on standings.
  const noMatchups = Boolean(board) && board.rows.length === 0 && board.standings?.length > 0;
  const effectiveView = noMatchups ? 'standings' : view;
  // The prop still forces rotation on for embeds/kiosks that want it.
  const autoRotate = (autoRotateProp ?? rotate) && !userPinned;

  // `board.playoff` is null outside the playoffs. It drives three things:
  // the extra round-label column (which costs row width, hence the scale
  // flag), the header's PLAYOFFS marker, and the row labels themselves.
  const playoff = board?.playoff ?? null;
  const scale = useBoardScale(board?.rows?.length ?? 5, { playoff: Boolean(playoff) });
  // Standings is a DIFFERENT layout with a different row count and a
  // different row shape, so it gets its own scale rather than inheriting
  // the matchup one. `scale.size` is derived from board.rows.length -- a
  // 10-team league is 5 matchup rows -- so handing it to StandingsBoard
  // sized 10 rows as though there were 5, leaving the bottom half of the
  // screen empty. The header sits above both views, so its height comes
  // out of the standings budget.
  const standingsScale = useStandingsScale(board?.standings?.length ?? 10, {
    headerH: Math.round(scale.headerSize * 1.06) + 20 + 24,
  });
  // AvatarFlap's spin effect depends on `pool`, so `pool` must stay
  // referentially stable across polls. avatarPool() builds a fresh array every
  // time; keying the memo on `board` would hand AvatarFlap a new array on every
  // poll, re-running its effect and eating the once-per-week slot-machine. The
  // league's avatar set almost never changes, so memoize on CONTENTS instead.
  const poolKey = useMemo(() => avatarPool(board).join('|'), [board]);
  const pool = useMemo(() => (poolKey ? poolKey.split('|') : []), [poolKey]);

  // auto-rotate between the two layouts on a TV
  useEffect(() => {
    if (!autoRotate || !board || noMatchups) return undefined;
    const t = setInterval(
      () => setView((v) => (v === 'matchups' ? 'standings' : 'matchups')),
      ROTATE_MS
    );
    return () => clearInterval(t);
  }, [autoRotate, board, noMatchups]);

  // keyboard: s toggles the view, r forces a refresh
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 's' || e.key === 'S') {
        pinView((v) => (v === 'matchups' ? 'standings' : 'matchups'));
      }
      if (e.key === 'r' || e.key === 'R') refresh();
      // 'a' for alternate -- 'r' is already refresh, and rotation is the
      // other thing you'd reach for on a TV without a mouse.
      if (e.key === 'a' || e.key === 'A') toggleRotate();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [refresh, pinView, toggleRotate]);

  // ---- state routing (§5.3) ----
  let content;
  if (!stored.ready && !leagueIdProp) {
    content = null;
  } else if (!leagueId) {
    // No league yet: the connect form IS the landing page. Masthead above,
    // site nav below, no click-through to reach the board.
    content = (
      <LeagueSetup
        initialUsername={routeUsername}
        onSelect={stored.setLeagueId}
        fetchImpl={fetchImpl}
        header={<BoardMasthead compact={scale.tier === 'compact'} />}
        footer={<SiteFooter />}
      />
    );
  } else if (status === 'loading') {
    content = <LoadingState size={scale.size} />;
  } else if (status === 'error' && !board) {
    content = (
      <InvalidLeagueState
        leagueId={leagueId}
        onReset={() => stored.setLeagueId(null)}
        size={scale.size}
      />
    );
  } else if (board && board.rows.length === 0 && !board.standings?.length) {
    // Truly nothing to show. A league with STANDINGS but no matchups is a
    // finished season, not an empty board -- that falls through and renders
    // the standings view below.
    content = <OffseasonState leagueName={board.leagueName} size={scale.size} />;
  } else if (board) {
    content = (
      <>
        <BoardHeader
          leagueName={board.leagueName}
          week={board.week}
          inSeason={board.inSeason !== false}
          playoff={playoff}
          size={scale.headerSize}
          nameWidth={scale.headerNameWidth}
        />
        {effectiveView === 'matchups' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: scale.rowGap }}>
            {board.rows.map((row, i) => (
              <Row
                key={row.bracketMatchId != null ? `b${row.bracketMatchId}` : `m${row.matchupId}`}
                row={row}
                week={week}
                pool={pool}
                size={scale.size}
                nameWidth={scale.nameWidth}
                showRoundLabel={Boolean(playoff)}
                revealDelay={i * 120}
              />
            ))}
          </div>
        ) : (
          <StandingsBoard
            standings={board.standings}
            week={week}
            pool={pool}
            size={standingsScale.size}
            nameWidth={standingsScale.nameWidth}
            showLabels={standingsScale.showLabels}
          />
        )}
      </>
    );
  }

  return (
    <div ref={shellRef} className={`solari-shell tier-${scale.tier}`} style={shell(isFullscreen)}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {content}
      </div>

      {leagueId && (
        <footer style={footer}>
          {/* The switch must reflect what is actually on screen (effectiveView),
              not the raw state -- an off-season board forces standings, and a
              switch reading MATCHUPS while standings are displayed looks broken.
              With no rows at all there is nothing to toggle, so it is hidden. */}
          {board && board.rows.length > 0 ? (
            <ViewSwitch
              value={effectiveView}
              onChange={pinView}
              size={scale.tier === 'tv' ? 1.25 : 1}
            />
          ) : <span />}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* ROTATE sits to the LEFT of the clock, deliberately quiet.
                It's a mode indicator more than an action -- most people
                set it once and forget it -- so it gets the same weight as
                the timestamp rather than competing with EXIT/LEAGUE.
                Amber when live, so a glance tells you why the board is
                moving on its own. Hidden when there's nothing to rotate
                between. */}
            {board && board.rows.length > 0 ? (
              <button
                type="button"
                onClick={toggleRotate}
                title={autoRotate
                  ? 'Board is alternating matchups and standings'
                  : 'Alternate matchups and standings automatically'}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 10,
                  letterSpacing: '.14em',
                  color: autoRotate ? '#ffb300' : '#3a3d44',
                  transition: 'color 200ms ease',
                }}
              >
                ROTATE{autoRotate ? ' ·' : ''}
              </button>
            ) : null}
            <span style={{ color: '#3a3d44', fontSize: 10, letterSpacing: '.14em' }}>
              {error && board ? 'RECONNECTING' : lastUpdated ? `UPD ${clock(lastUpdated)}` : ''}
            </span>
            <ShareChip url={stored.shareUrl} />
            <Chip onClick={toggle}>{isFullscreen ? 'EXIT' : 'FULLSCREEN'}</Chip>
            <Chip onClick={() => stored.setLeagueId(null)}>LEAGUE</Chip>
            {!isFullscreen && (
              <>
                <span style={{ color: '#22252a' }}>|</span>
                <Chip onClick={() => { window.location.hash = 'generate'; }}>NAMES</Chip>
                <Chip onClick={() => { window.location.hash = 'play'; }}>PLAY</Chip>
              </>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

function clock(ms) {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Copies the board's URL. The link IS the product's distribution -- someone
// drops it in the league group chat and everyone opens the same board -- so
// it gets a real control, not a buried menu item.
function ShareChip({ url }) {
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // clipboard blocked (http, older browser): select-and-prompt fallback
      window.prompt('Copy this link:', url);
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return <Chip onClick={copy} active={copied}>{copied ? 'COPIED' : 'COPY LINK'}</Chip>;
}

function Chip({ children, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        color: active ? '#ffb300' : '#3a3d44',
        fontSize: 10,
        letterSpacing: '.2em',
        fontWeight: 700,
        cursor: 'pointer',
        padding: '4px 2px',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

// Fullscreen padding is CAPPED, not a raw percentage.
//
// It used to be `4vh 4vw 2vh`. On a 2560px screen 4vw is 102px per side --
// 205px of the row's width budget spent on empty margin, and it grows with
// the display. That was the visible dead space to the left of a fullscreen
// board. Worse, computeScale budgeted a flat 48px per side, so the two
// disagreed and the board sat off-center inside its own padding.
//
// The horizontal gutter is now a fixed 48px, matching BOARD_PAD_X in
// useBoardScale. Vertical keeps a percentage but capped, since vertical
// room is rarely the binding constraint. A departures board should run
// close to edge-to-edge; the margin is a frame, not a feature.
const shell = (fs) => ({
  minHeight: '100vh',
  background: '#07080a',
  color: '#e8e8e8',
  display: 'flex',
  flexDirection: 'column',
  padding: fs
    ? `min(4vh, 44px) ${BOARD_PAD_X}px min(2vh, 22px)`
    : `32px ${BOARD_PAD_X - 20}px 16px`,
  boxSizing: 'border-box',
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
});

const footer = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 28,
  paddingTop: 14,
  borderTop: '1px solid #14161a',
};
